import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const MONTHS_FR = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const formatTime = (iso) => new Date(iso).toLocaleTimeString("fr-CH", { hour:"2-digit", minute:"2-digit", timeZone:"Europe/Zurich" });

export default function CalendarTab({ onSelectAppointment }) {
    const [month, setMonth] = useState(startOfMonth(new Date()));
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const first = startOfMonth(month);
        const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
        try {
            const r = await api.get("/admin/appointments", { params: { from: ymd(first), to: ymd(last) } });
            setAppointments(r.data || []);
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const m = {};
        for (const a of appointments) {
            const date = new Date(a.start_at);
            const k = ymd(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
            (m[k] = m[k] || []).push(a);
        }
        for (const k in m) m[k].sort((a, b) => a.start_at.localeCompare(b.start_at));
        return m;
    }, [appointments]);

    const cells = useMemo(() => {
        const first = startOfMonth(month);
        const startWeekday = (first.getDay() + 6) % 7;
        const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        const out = [];
        for (let i = 0; i < startWeekday; i++) out.push(null);
        for (let i = 1; i <= daysInMonth; i++) out.push(new Date(month.getFullYear(), month.getMonth(), i));
        return out;
    }, [month]);

    return (
        <div data-testid="admin-calendar">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display text-2xl text-brand-ink">Calendrier global</h2>
                    <p className="font-sans text-sm text-brand-ink/60 mt-1">
                        {appointments.length} rendez-vous ce mois
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                        className="h-9 w-9 rounded-full border border-brand-line flex items-center justify-center hover:bg-brand-mint/30">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <p className="font-display text-lg w-44 text-center capitalize">
                        {MONTHS_FR[month.getMonth()]} {month.getFullYear()}
                    </p>
                    <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                        className="h-9 w-9 rounded-full border border-brand-line flex items-center justify-center hover:bg-brand-mint/30">
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {loading && <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-primary" /></div>}

            <div className="grid grid-cols-7 gap-2 text-center text-xs font-sans text-brand-ink/50 mb-2">
                {DAYS_FR.map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-2">
                {cells.map((d, i) => {
                    if (!d) return <div key={`empty-${i}`} className="min-h-[110px]" />;
                    const k = ymd(d);
                    const list = grouped[k] || [];
                    const isToday = k === ymd(new Date());
                    return (
                        <div key={k} className={`bg-white border ${isToday ? "border-brand-primary" : "border-brand-line"} p-2 min-h-[110px]`}>
                            <div className={`text-xs font-sans ${isToday ? "text-brand-primary font-semibold" : "text-brand-ink/60"}`}>
                                {d.getDate()}
                            </div>
                            <div className="mt-2 space-y-1">
                                {list.slice(0, 3).map((a) => (
                                    <button key={a.id}
                                        onClick={() => onSelectAppointment?.(a)}
                                        className="w-full text-left text-[11px] px-1.5 py-1 truncate rounded hover:opacity-80"
                                        style={{ background: "rgba(78,109,111,0.1)", color: "#3a5355" }}
                                        title={`${a.customer_name} — ${a.type_name}`}>
                                        <span className="font-medium">{formatTime(a.start_at)}</span> {a.customer_name}
                                    </button>
                                ))}
                                {list.length > 3 && <p className="text-[10px] text-brand-ink/40">+{list.length - 3} autres</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
